import { useState, useEffect, useMemo } from 'react';
import { format, addDays, startOfToday } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Users, User,
  Search, Heart, Gift, Briefcase, GraduationCap, Users2, Home, Star, Cake,
  Gem, Ban, AlertTriangle, MessageSquare, Accessibility, Baby, Utensils,
  Check, X, Sparkles, Phone, Mail, Send, Loader2, Zap, Globe
} from 'lucide-react';

// Idiomas soportados (mismos que CartaExpressWidget)
const LANGUAGES = [
  { code:'es', label:'Español', flag:'🇪🇸' }, { code:'en', label:'English', flag:'🇺🇸' },
  { code:'fr', label:'Français', flag:'🇫🇷' }, { code:'it', label:'Italiano', flag:'🇮🇹' },
  { code:'pt', label:'Português', flag:'🇵🇹' }, { code:'de', label:'Deutsch', flag:'🇩🇪' },
  { code:'zh', label:'中文', flag:'🇨🇳' }, { code:'ko', label:'한국어', flag:'🇰🇷' },
  { code:'ja', label:'日本語', flag:'🇯🇵' }, { code:'ar', label:'العربية', flag:'🇸🇦' },
];

const RW_T = {
  es:{ whoFor:'¿A nombre de quién la reserva?', forYou:'Para ti', someoneElse:'Alguien más', external:'Fuera de OkGo', occasion:'¿Cuál es la ocasión?', allergies:'Alergias', requests:'Petición especial', requirements:'Requerimientos', date:'Fecha', time:'Hora', guests:'Personas', back:'Atrás', next:'Siguiente', confirm:'Confirmar reserva', step1:'Tus datos', step2:'Fecha y hora', step3:'Confirmar', selectLang:'Idioma' },
  en:{ whoFor:'Who is the reservation for?', forYou:'For you', someoneElse:'Someone else', external:'Outside OkGo', occasion:'What is the occasion?', allergies:'Allergies', requests:'Special request', requirements:'Requirements', date:'Date', time:'Time', guests:'Guests', back:'Back', next:'Next', confirm:'Confirm reservation', step1:'Your details', step2:'Date & time', step3:'Confirm', selectLang:'Language' },
  fr:{ whoFor:'À quel nom la réservation?', forYou:'Pour toi', someoneElse:'Quelqu\'un d\'autre', external:'Hors OkGo', occasion:'Quelle est l\'occasion?', allergies:'Allergies', requests:'Demande spéciale', requirements:'Exigences', date:'Date', time:'Heure', guests:'Invités', back:'Retour', next:'Suivant', confirm:'Confirmer la réservation', step1:'Vos données', step2:'Date et heure', step3:'Confirmer', selectLang:'Langue' },
  it:{ whoFor:'A nome di chi la prenotazione?', forYou:'Per te', someoneElse:'Qualcun altro', external:'Fuori OkGo', occasion:'Qual è l\'occasione?', allergies:'Allergie', requests:'Richiesta speciale', requirements:'Esigenze', date:'Data', time:'Ora', guests:'Ospiti', back:'Indietro', next:'Avanti', confirm:'Conferma prenotazione', step1:'I tuoi dati', step2:'Data e ora', step3:'Conferma', selectLang:'Lingua' },
  pt:{ whoFor:'Em nome de quem é a reserva?', forYou:'Para você', someoneElse:'Outra pessoa', external:'Fora do OkGo', occasion:'Qual é a ocasião?', allergies:'Alergias', requests:'Pedido especial', requirements:'Requisitos', date:'Data', time:'Hora', guests:'Pessoas', back:'Voltar', next:'Próximo', confirm:'Confirmar reserva', step1:'Seus dados', step2:'Data e hora', step3:'Confirmar', selectLang:'Idioma' },
  de:{ whoFor:'Auf wessen Namen?', forYou:'Für dich', someoneElse:'Jemand anderes', external:'Außerhalb OkGo', occasion:'Was ist der Anlass?', allergies:'Allergien', requests:'Sonderwunsch', requirements:'Anforderungen', date:'Datum', time:'Uhrzeit', guests:'Gäste', back:'Zurück', next:'Weiter', confirm:'Reservierung bestätigen', step1:'Deine Daten', step2:'Datum & Uhrzeit', step3:'Bestätigen', selectLang:'Sprache' },
  zh:{ whoFor:'预订人姓名?', forYou:'给您', someoneElse:'其他人', external:'OkGo 外', occasion:'什么场合?', allergies:'过敏', requests:'特殊要求', requirements:'要求', date:'日期', time:'时间', guests:'人数', back:'返回', next:'下一步', confirm:'确认预订', step1:'您的资料', step2:'日期时间', step3:'确认', selectLang:'语言' },
  ko:{ whoFor:'누구를 위한 예약인가요?', forYou:'당신을 위해', someoneElse:'다른 사람', external:'OkGo 외부', occasion:'어떤 행사인가요?', allergies:'알레르기', requests:'특별 요청', requirements:'요구사항', date:'날짜', time:'시간', guests:'인원', back:'뒤로', next:'다음', confirm:'예약 확정', step1:'정보', step2:'날짜와 시간', step3:'확인', selectLang:'언어' },
  ja:{ whoFor:'予約は誰の名前で?', forYou:'あなたへ', someoneElse:'他の方', external:'OkGo 外', occasion:'機会は何ですか?', allergies:'アレルギー', requests:'特別なリクエスト', requirements:'要件', date:'日付', time:'時間', guests:'人数', back:'戻る', next:'次へ', confirm:'予約を確定', step1:'情報', step2:'日時', step3:'確認', selectLang:'言語' },
  ar:{ whoFor:'باسم من الحجز?', forYou:'لك', someoneElse:'شخص آخر', external:'خارج OkGo', occasion:'ما المناسبة?', allergies:'الحساسية', requests:'طلب خاص', requirements:'المتطلبات', date:'التاريخ', time:'الوقت', guests:'الضيوف', back:'رجوع', next:'التالي', confirm:'تأكيد الحجز', step1:'بياناتك', step2:'التاريخ والوقت', step3:'تأكيد', selectLang:'اللغة' },
};

// Ocasiones predefinidas con iconos
const DEFAULT_OCCASIONS = [
  { id: 'anniversary', label: 'Aniversario', icon: Heart },
  { id: 'romantic', label: 'Cita romántica', icon: Heart },
  { id: 'birthday', label: 'Cumpleaños', icon: Cake },
  { id: 'friends', label: 'Encuentro entre amigos', icon: Users2 },
  { id: 'graduation', label: 'Graduación', icon: GraduationCap },
  { id: 'business', label: 'Negocios', icon: Briefcase },
  { id: 'proposal', label: 'Propuesta', icon: Gem },
  { id: 'family', label: 'Reunión familiar', icon: Home },
  { id: 'none', label: 'No la hay', icon: Ban },
];

// Requerimientos especiales
const SPECIAL_REQUIREMENTS = [
  { id: 'accessibility', label: 'Apoyo especial', icon: Accessibility },
  { id: 'baby_seat', label: 'Silla de bebé', icon: Baby },
];

export default function ReservationWidget({
  restaurantId,
  restaurantName = 'Restaurante',
  logoUrl,
  primaryColor = '#F97316',
  customOccasions = [],
  enabledOccasions = DEFAULT_OCCASIONS.map(o => o.id),
  cancellationPolicy = '',
  language = 'es',
  onSuccess,
  onClose,
  API_URL = process.env.REACT_APP_BACKEND_URL,
}) {
  // Idioma + traducciones
  const [lang, setLang] = useState(language || 'es');
  const [langOpen, setLangOpen] = useState(false);
  const t = RW_T[lang] || RW_T.es;
  const isRtl = lang === 'ar';
  useEffect(() => { setLang(language || 'es'); }, [language]);
  // Cierra el dropdown al hacer click fuera
  useEffect(() => {
    if (!langOpen) return;
    const close = (e) => { if (!e.target.closest('[data-rw-lang]')) setLangOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [langOpen]);

  // Estado del wizard
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [userPreferences, setUserPreferences] = useState(null);
  const [quickReserveMode, setQuickReserveMode] = useState(false);

  // Datos del formulario - Paso 1
  const [reservationType, setReservationType] = useState('self'); // self, okuser, external
  const [okUserNickname, setOkUserNickname] = useState('');
  const [okUserFound, setOkUserFound] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [guestForNickname, setGuestForNickname] = useState('');
  const [guestForFound, setGuestForFound] = useState(null);
  const [externalGuest, setExternalGuest] = useState({ name: '', email: '', whatsapp: '' });
  const [selectedOccasion, setSelectedOccasion] = useState(null);
  const [customOccasionText, setCustomOccasionText] = useState('');
  const [allergies, setAllergies] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [customRequirement, setCustomRequirement] = useState('');

  // Datos del formulario - Paso 2
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Genera los próximos 5 días
  const today = startOfToday();
  const quickDates = Array.from({ length: 5 }, (_, i) => addDays(today, i));

  // Cargar preferencias del usuario para Reserva Rápida
  const loadUserPreferences = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${userId}/preferences`);
      if (response.ok) {
        const prefs = await response.json();
        setUserPreferences(prefs);
        
        // Pre-cargar preferencias automáticamente
        if (prefs.frequent_allergies) setAllergies(prefs.frequent_allergies);
        if (prefs.frequent_occasion) setSelectedOccasion(prefs.frequent_occasion);
        if (prefs.usual_party_size) setPartySize(prefs.usual_party_size);
        if (prefs.special_requirements?.length) setSelectedRequirements(prefs.special_requirements);
        
        setQuickReserveMode(true);
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
    }
  };

  // Buscar usuario OkUser
  const searchOkUser = async (nickname, isForGuest = false) => {
    if (!nickname || nickname.length < 2) return;
    
    setSearchingUser(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/search?nickname=${encodeURIComponent(nickname)}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const foundUser = data[0];
          if (isForGuest) {
            setGuestForFound(foundUser);
          } else {
            setOkUserFound(foundUser);
            // Cargar preferencias para Reserva Rápida
            if (foundUser.id) {
              loadUserPreferences(foundUser.id);
            }
          }
        } else {
          if (isForGuest) {
            setGuestForFound(null);
          } else {
            setOkUserFound(null);
            setUserPreferences(null);
            setQuickReserveMode(false);
          }
        }
      }
    } catch (err) {
      console.error('Error searching user:', err);
    } finally {
      setSearchingUser(false);
    }
  };

  // Cargar slots disponibles cuando se selecciona fecha
  useEffect(() => {
    if (selectedDate && restaurantId) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate, restaurantId]);

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/restaurants/${restaurantId}/availability?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        // Map available_times to time_slots format
        const slots = (data.available_times || []).map((slot, idx) => ({
          id: `slot-${idx}`,
          start_time: slot.time,
          available: slot.available
        }));
        setAvailableSlots(slots);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoading(false);
    }
  };

  // Formatear hora a 12h
  const formatTime12h = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Combinar ocasiones predefinidas con personalizadas (memoized)
  const allOccasions = useMemo(() => [
    ...DEFAULT_OCCASIONS.filter(o => enabledOccasions.includes(o.id)),
    ...customOccasions.map(o => ({
      ...o,
      icon: Star // Default icon for custom occasions
    }))
  ], [enabledOccasions, customOccasions]);

  // Memoized available time slots
  const filteredAvailableSlots = useMemo(() => {
    return availableSlots.filter(s => s.available);
  }, [availableSlots]);

  // Validar paso actual
  const canProceed = () => {
    if (currentStep === 1) {
      if (reservationType === 'self' && !okUserFound) return false;
      if (reservationType === 'okuser' && !guestForFound) return false;
      if (reservationType === 'external' && (!externalGuest.name || !externalGuest.whatsapp)) return false;
      return true;
    }
    if (currentStep === 2) {
      return partySize > 0 && selectedDate && selectedTime;
    }
    return true;
  };

  // Enviar reservación
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const reservationData = {
        restaurant_id: restaurantId,
        party_size: partySize,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        occasion: selectedOccasion,
        custom_occasion: customOccasionText,
        allergies,
        special_requests: specialRequests,
        special_requirements: selectedRequirements,
        custom_requirement: customRequirement,
        reservation_type: reservationType,
        guest_name: reservationType === 'self' 
          ? okUserFound?.name 
          : reservationType === 'okuser' 
            ? guestForFound?.name 
            : externalGuest.name,
        guest_email: reservationType === 'external' ? externalGuest.email : null,
        guest_whatsapp: reservationType === 'external' ? externalGuest.whatsapp : null,
        okuser_id: reservationType === 'self' 
          ? okUserFound?.id 
          : reservationType === 'okuser' 
            ? guestForFound?.id 
            : null,
      };

      const response = await fetch(`${API_URL}/api/reservations/widget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData),
      });

      if (response.ok) {
        const result = await response.json();
        onSuccess?.(result);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al crear la reservación');
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  // Renderizar calendario - BOTONES GRANDES
  const renderCalendar = () => {
    const daysInMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + 1,
      0
    ).getDate();
    
    const firstDayOfMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      1
    ).getDay();

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-3" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
      const isPast = date < today;
      const isSelected = selectedDate && format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
      const isToday = format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');

      days.push(
        <button
          key={day}
          onClick={() => {
            if (!isPast) {
              setSelectedDate(date);
            }
          }}
          disabled={isPast}
          className={`w-full aspect-square flex items-center justify-center text-xl font-bold rounded-2xl transition-all ${
            isPast 
              ? 'text-gray-300 cursor-not-allowed' 
              : isSelected 
                ? 'text-white shadow-lg scale-105' 
                : isToday
                  ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                  : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={isSelected ? { backgroundColor: primaryColor, boxShadow: `0 4px 12px ${primaryColor}40` } : {}}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header con Logo (200% más grande) */}
      <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-6 text-center" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}>
        {/* Selector de idioma */}
        <div data-rw-lang style={{ position:'absolute', top:12, right:12, zIndex:20 }}>
          <button onClick={() => setLangOpen(o => !o)}
            style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.95)', border:'none', borderRadius:999, padding:'5px 10px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#374151', boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}
            title={t.selectLang}>
            <Globe className="w-3.5 h-3.5" />
            {LANGUAGES.find(l => l.code === lang)?.flag} {lang.toUpperCase()}
          </button>
          {langOpen && (
            <div style={{ position:'absolute', top:36, right:0, background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', minWidth:160, maxHeight:280, overflowY:'auto', zIndex:30 }}>
              {LANGUAGES.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                  style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'8px 12px', background:lang===l.code?`${primaryColor}15`:'transparent', border:'none', cursor:'pointer', fontSize:12.5, fontWeight:lang===l.code?700:400, color:lang===l.code?primaryColor:'#374151', textAlign:'left' }}>
                  <span>{l.flag}</span><span>{l.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt={restaurantName} 
            className="w-32 h-32 mx-auto object-contain rounded-2xl bg-white/10 p-2"
          />
        ) : (
          <div className="w-32 h-32 mx-auto bg-white/20 rounded-2xl flex items-center justify-center">
            <Utensils className="w-16 h-16 text-white" />
          </div>
        )}
        <h1 className="text-xl font-bold text-white mt-3">{restaurantName}</h1>
        
        {/* Indicador de pasos */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`h-2 rounded-full transition-all ${
                step === currentStep 
                  ? 'w-8 bg-white' 
                  : step < currentStep 
                    ? 'w-4 bg-white/70' 
                    : 'w-4 bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Contenido del paso */}
      <div className="p-6 max-h-[60vh] overflow-y-auto">
        {/* ==================== PASO 1 ==================== */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* ¿A nombre de quién? */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-5 h-5" style={{ color: primaryColor }} />
                {t.whoFor}
              </h3>
              
              <div className="grid grid-cols-3 gap-2 mb-4">
                <button
                  onClick={() => setReservationType('self')}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    reservationType === 'self' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={reservationType === 'self' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                >
                  <User className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">{t.forYou}</span>
                </button>
                <button
                  onClick={() => setReservationType('okuser')}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    reservationType === 'okuser' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={reservationType === 'okuser' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                >
                  <Gift className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">{t.someoneElse}</span>
                </button>
                <button
                  onClick={() => setReservationType('external')}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    reservationType === 'external' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={reservationType === 'external' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                >
                  <Users className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">{t.external}</span>
                </button>
              </div>

              {/* Input según tipo */}
              {reservationType === 'self' && (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="OkUser - Coloca tu nombre de usuario o nickname"
                      value={okUserNickname}
                      onChange={(e) => {
                        setOkUserNickname(e.target.value);
                        setOkUserFound(null);
                        setUserPreferences(null);
                        setQuickReserveMode(false);
                      }}
                      onBlur={() => searchOkUser(okUserNickname)}
                      className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': primaryColor }}
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    {searchingUser && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                    )}
                  </div>
                  {okUserFound && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center overflow-hidden">
                        {okUserFound.photo_url ? (
                          <img src={okUserFound.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800">{okUserFound.name}</p>
                        <p className="text-xs text-green-600">@{okUserFound.nickname}</p>
                      </div>
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                  
                  {/* Indicador de Reserva Rápida */}
                  {quickReserveMode && userPreferences && (
                    <div className="p-3 rounded-xl border-2 border-dashed" style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}08` }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4" style={{ color: primaryColor }} />
                        <span className="text-sm font-bold" style={{ color: primaryColor }}>Reserva Rápida</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Hemos pre-cargado tus preferencias basadas en tus {userPreferences.total_reservations} reservaciones anteriores.
                      </p>
                      {userPreferences.favorite_table && (
                        <p className="text-xs text-gray-500 mt-1">
                          Mesa favorita: {userPreferences.favorite_table}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {reservationType === 'okuser' && (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Busca a la persona por su nickname"
                      value={guestForNickname}
                      onChange={(e) => {
                        setGuestForNickname(e.target.value);
                        setGuestForFound(null);
                      }}
                      onBlur={() => searchOkUser(guestForNickname, true)}
                      className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                  {guestForFound && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center overflow-hidden">
                        {guestForFound.photo_url ? (
                          <img src={guestForFound.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800">{guestForFound.name}</p>
                        <p className="text-xs text-green-600">@{guestForFound.nickname}</p>
                      </div>
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </div>
              )}

              {reservationType === 'external' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Nombre completo"
                    value={externalGuest.name}
                    onChange={(e) => setExternalGuest({ ...externalGuest, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                  />
                  <input
                    type="email"
                    placeholder="Correo electrónico"
                    value={externalGuest.email}
                    onChange={(e) => setExternalGuest({ ...externalGuest, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                  />
                  <input
                    type="tel"
                    placeholder="WhatsApp"
                    value={externalGuest.whatsapp}
                    onChange={(e) => setExternalGuest({ ...externalGuest, whatsapp: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                  />
                </div>
              )}
            </div>

            {/* ¿Cuál es la ocasión? */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Cuál es la ocasión?
              </h3>
              <p className="text-gray-500 text-sm mb-3">¿Celebras algo en especial?</p>
              
              <div className="grid grid-cols-3 gap-2 mb-3">
                {allOccasions.map((occasion) => {
                  const Icon = occasion.icon || Star;
                  const isSelected = selectedOccasion === occasion.id;
                  return (
                    <button
                      key={occasion.id}
                      onClick={() => setSelectedOccasion(occasion.id)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={isSelected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? '' : 'text-gray-500'}`} style={isSelected ? { color: primaryColor } : {}} />
                      <span className="text-xs font-medium">{occasion.label}</span>
                    </button>
                  );
                })}
              </div>
              
              <input
                type="text"
                placeholder="¿Deseas compartir algún otro motivo en especial?"
                value={customOccasionText}
                onChange={(e) => setCustomOccasionText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
              />
            </div>

            {/* Alergias */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Alguna restricción alérgica?
              </h3>
              <p className="text-gray-500 text-sm mb-3">Tuya o de alguno de tus acompañantes</p>
              <textarea
                placeholder="Por favor compártenos los alérgenos a los que tienes alergia."
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 resize-none text-sm"
              />
            </div>

            {/* Peticiones especiales */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Alguna petición especial?
              </h3>
              <p className="text-gray-500 text-sm mb-3">Nos pondremos en contacto contigo si es necesario</p>
              <textarea
                placeholder="Comparte cualquier comentario o petición especial."
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 resize-none text-sm"
              />
            </div>

            {/* Requerimientos especiales */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Accessibility className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Algún requerimiento especial?
              </h3>
              <p className="text-gray-500 text-sm mb-3">Estaremos preparados para su llegada</p>
              
              <div className="flex gap-2 mb-3">
                {SPECIAL_REQUIREMENTS.map((req) => {
                  const Icon = req.icon;
                  const isSelected = selectedRequirements.includes(req.id);
                  return (
                    <button
                      key={req.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedRequirements(selectedRequirements.filter(r => r !== req.id));
                        } else {
                          setSelectedRequirements([...selectedRequirements, req.id]);
                        }
                      }}
                      className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={isSelected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? '' : 'text-gray-500'}`} style={isSelected ? { color: primaryColor } : {}} />
                      <span className="text-xs font-medium">{req.label}</span>
                    </button>
                  );
                })}
              </div>
              
              <input
                type="text"
                placeholder="Agrega tu requerimiento específico"
                value={customRequirement}
                onChange={(e) => setCustomRequirement(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
              />
            </div>

            {/* Política de cancelación */}
            {cancellationPolicy && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 font-medium">Política de cancelación</p>
                <p className="text-sm text-gray-700">{cancellationPolicy}</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== PASO 2 ==================== */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Número de personas */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Cuántas personas?
              </h3>
              
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setPartySize(Math.max(1, partySize - 1))}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-gray-100"
                >
                  -
                </button>
                <span className="text-4xl font-bold w-16 text-center" style={{ color: primaryColor }}>
                  {partySize}
                </span>
                <button
                  onClick={() => setPartySize(Math.min(20, partySize + 1))}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-gray-100"
                >
                  +
                </button>
              </div>
            </div>

            {/* Selección de fecha */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" style={{ color: primaryColor }} />
                ¿Qué día?
              </h3>
              
              {/* Quick dates - próximos 5 días */}
              <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                {quickDates.map((date) => {
                  const isSelected = selectedDate && format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                  return (
                    <button
                      key={date.toISOString()}
                      onClick={() => setSelectedDate(date)}
                      className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 text-center transition-all min-w-[80px] ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-50 text-orange-600' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={isSelected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10`, color: primaryColor } : {}}
                    >
                      <p className="text-xs font-medium uppercase">
                        {format(date, 'EEE', { locale: es })}
                      </p>
                      <p className="text-lg font-bold">
                        {format(date, 'd')}
                      </p>
                    </button>
                  );
                })}
                
                {/* Botón calendario */}
                <button
                  onClick={() => setShowCalendar(true)}
                  className="flex-shrink-0 px-4 py-3 rounded-xl border-2 border-gray-200 text-center transition-all min-w-[80px] hover:border-gray-300"
                >
                  <CalendarIcon className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                  <p className="text-xs font-medium">Más</p>
                </button>
              </div>

              {/* Modal calendario - GRANDE con botones grandes */}
              {showCalendar && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                  <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
                    <div className="flex items-center justify-between mb-6">
                      <button
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                        className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <h4 className="text-xl font-bold text-gray-900 capitalize">
                        {format(calendarMonth, 'MMMM yyyy', { locale: es })}
                      </h4>
                      <button
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                        className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-7 gap-2 mb-3">
                      {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, i) => (
                        <div key={i} className="text-center text-sm font-bold text-gray-400 py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-2">
                      {renderCalendar()}
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg hover:bg-gray-200 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => setShowCalendar(false)}
                        disabled={!selectedDate}
                        className="flex-1 py-4 text-white rounded-2xl font-bold text-lg transition-colors disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Selección de hora */}
            {selectedDate && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" style={{ color: primaryColor }} />
                  ¿A qué hora?
                </h3>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {filteredAvailableSlots.map((slot) => {
                      const isSelected = selectedTime === slot.start_time;
                      return (
                        <button
                          key={slot.id}
                          onClick={() => setSelectedTime(slot.start_time)}
                          className={`py-3 rounded-xl border-2 text-center font-medium transition-all ${
                            isSelected 
                              ? 'border-orange-500 bg-orange-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={isSelected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10`, color: primaryColor } : {}}
                        >
                          {formatTime12h(slot.start_time)}
                        </button>
                      );
                    })}
                    
                    {filteredAvailableSlots.length === 0 && (
                      <p className="col-span-4 text-center py-8 text-gray-500">
                        No hay horarios disponibles para esta fecha
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== PASO 3 - CONFIRMACIÓN ==================== */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h3 className="font-bold text-gray-900 text-lg mb-4">Confirma tu reservación</h3>
            
            {error && (
              <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              {/* Resumen de datos */}
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Nombre</span>
                <span className="font-medium">
                  {reservationType === 'self' 
                    ? okUserFound?.name 
                    : reservationType === 'okuser' 
                      ? guestForFound?.name 
                      : externalGuest.name}
                </span>
                <button 
                  onClick={() => setCurrentStep(1)}
                  className="text-orange-500 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Editar
                </button>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Personas</span>
                <span className="font-medium">{partySize}</span>
                <button 
                  onClick={() => setCurrentStep(2)}
                  className="text-orange-500 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Editar
                </button>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Fecha</span>
                <span className="font-medium">
                  {selectedDate && format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                </span>
                <button 
                  onClick={() => setCurrentStep(2)}
                  className="text-orange-500 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Editar
                </button>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Hora</span>
                <span className="font-medium">{selectedTime && formatTime12h(selectedTime)}</span>
                <button 
                  onClick={() => setCurrentStep(2)}
                  className="text-orange-500 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Editar
                </button>
              </div>

              {selectedOccasion && (
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Ocasión</span>
                  <span className="font-medium">
                    {allOccasions.find(o => o.id === selectedOccasion)?.label || customOccasionText}
                  </span>
                  <button 
                    onClick={() => setCurrentStep(1)}
                    className="text-orange-500 text-sm font-medium"
                    style={{ color: primaryColor }}
                  >
                    Editar
                  </button>
                </div>
              )}

              {allergies && (
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <p className="text-xs font-medium text-yellow-800 mb-1">Alergias</p>
                  <p className="text-sm text-yellow-700">{allergies}</p>
                </div>
              )}

              {specialRequests && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-xs font-medium text-blue-800 mb-1">Peticiones especiales</p>
                  <p className="text-sm text-blue-700">{specialRequests}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer con botones de navegación */}
      <div className="p-6 border-t bg-gray-50">
        <div className="flex gap-3">
          {currentStep > 1 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex-1 py-4 border-2 border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              {t.back}
            </button>
          )}
          
          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="flex-1 py-4 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
            >
              {t.next}
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-4 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t.confirm}
                </>
              )}
            </button>
          )}
        </div>
        
        {/* Footer OkReserva */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Powered by <span className="font-semibold text-orange-500">OkGo</span>
          </p>
        </div>
      </div>
    </div>
  );
}
